import { PRODUCT } from './types'
import { sify } from 'chinese-conv'
import _ from 'lodash'

import { productSave } from '../Component/ProductCreate/Product/productSave'
import store from '../store'
import { getI18nObjValue } from '../helper/i18n'

export const PRODUCT_CATEGORY_ACTIONS = {
    SET_CITY: 'SET_CITY',
    SET_REGION: 'SET_REGION',
}

const MEDIA_LANGUAGE_KEY_MAP = {
    EN: 'en',
    en: 'en',
    ZH_HK: 'zh-HK',
    'zh-HK': 'zh-HK',
    zh_HK: 'zh-HK',
    ZH_TW: 'zh-TW',
    'zh-TW': 'zh-TW',
    zh_TW: 'zh-TW',
    ZH_CN: 'zh-CN',
    'zh-CN': 'zh-CN',
    zh_CN: 'zh-CN',
}

const DEFAULT_MEDIA_LANGUAGES = ['en', 'zh-HK', 'zh-TW', 'zh-CN']

const LEGACY_IMAGE_TYPE_TO_MEDIA_KEY = {
    ITEM_ICON: 'iconImage',
    ITEM_DETAIL: 'detailImageList',
    // ITEM_PURCHASED_DETAIL: 'cashCouponDetailPurchasedImage',
    CASH_COUPON_DETAIL: 'cashCouponDetailImage',
    CASH_COUPON_PURCHASED_DETAIL: 'cashCouponDetailPurchasedImage',
    CASH_COUPON_LEFT_IMAGE: 'cashCouponLeftImage',
}

const normalizeMediaLanguageKey = (languageKey) => MEDIA_LANGUAGE_KEY_MAP[languageKey] || languageKey

const hasMediaValue = (mediaValue) => {
    if (!mediaValue) {
        return false
    }

    if (Array.isArray(mediaValue)) {
        return mediaValue.length > 0
    }

    if (typeof mediaValue === 'object') {
        return Object.keys(mediaValue).length > 0
    }

    return true
}

const normalizeMediaLanguageMap = (mediaValue) => {
    if (!mediaValue || typeof mediaValue !== 'object' || Array.isArray(mediaValue)) {
        return mediaValue
    }

    if (mediaValue.cosFileUrl || mediaValue.cosFileKey) {
        return mediaValue
    }

    return Object.entries(mediaValue).reduce((normalizedMedia, [languageKey, mediaData]) => {
        normalizedMedia[normalizeMediaLanguageKey(languageKey)] = mediaData
        return normalizedMedia
    }, {})
}

const normalizeDetailImageList = (detailImageListValue) => {
    if (!Array.isArray(detailImageListValue)) {
        return detailImageListValue
    }

    return detailImageListValue
        .filter((detailItem) => detailItem && typeof detailItem === 'object' && !Array.isArray(detailItem))
        .map((detailItem) => normalizeMediaLanguageMap(detailItem))
}

const toLegacyLanguageMediaMap = (imageItem) => {
    const mediaItem = _.cloneDeep(imageItem)

    if (!hasMediaValue(mediaItem)) {
        return null
    }

    if (mediaItem.language) {
        const languageKey = normalizeMediaLanguageKey(mediaItem.language)
        delete mediaItem.language
        return {
            [languageKey]: mediaItem,
        }
    }

    return DEFAULT_MEDIA_LANGUAGES.reduce((languageMap, languageKey) => {
        languageMap[languageKey] = _.cloneDeep(mediaItem)
        return languageMap
    }, {})
}

const toLegacyDetailImageListItem = (imageItem) => {
    return toLegacyLanguageMediaMap(imageItem)
}

const findDetailListIndexByDisplaySequence = (detailImageList, detailMediaMap) => {
    if (!Array.isArray(detailImageList) || !detailMediaMap || typeof detailMediaMap !== 'object') {
        return -1
    }

    const detailMediaSample = Object.values(detailMediaMap).find((value) => value && typeof value === 'object')
    const displaySequence = detailMediaSample?.displaySequence

    if (displaySequence === undefined || displaySequence === null) {
        return -1
    }

    return detailImageList.findIndex((detailItem) => {
        if (!detailItem || typeof detailItem !== 'object' || Array.isArray(detailItem)) {
            return false
        }

        return Object.values(detailItem).some(
            (languageMedia) => languageMedia && typeof languageMedia === 'object' && languageMedia.displaySequence === displaySequence
        )
    })
}

const mergeLegacyDetailImageIntoList = (detailImageList, mappedDetailItem) => {
    if (!mappedDetailItem || typeof mappedDetailItem !== 'object') {
        return detailImageList
    }

    const normalizedList = Array.isArray(detailImageList) ? _.cloneDeep(detailImageList) : []
    const mergeIndex = findDetailListIndexByDisplaySequence(normalizedList, mappedDetailItem)

    if (mergeIndex >= 0) {
        normalizedList[mergeIndex] = {
            ...(normalizedList[mergeIndex] || {}),
            ...mappedDetailItem,
        }
        return normalizedList
    }

    if (normalizedList.length === 0) {
        normalizedList.push(mappedDetailItem)
        return normalizedList
    }

    const firstLanguageKey = Object.keys(mappedDetailItem)[0]
    const hasLanguage = firstLanguageKey && !normalizedList[0]?.[firstLanguageKey]

    if (hasLanguage) {
        normalizedList[0] = {
            ...(normalizedList[0] || {}),
            ...mappedDetailItem,
        }
        return normalizedList
    }

    normalizedList.push(mappedDetailItem)
    return normalizedList
}

const convertLegacyImageListToImageMedia = (imageList, imageMedia) => {
    const normalizedImageMedia = _.cloneDeep(imageMedia || {})

    if (!Array.isArray(imageList) || imageList.length === 0) {
        return normalizedImageMedia
    }

    imageList.forEach((imageItem) => {
        const mediaKey = LEGACY_IMAGE_TYPE_TO_MEDIA_KEY[imageItem?.type]
        if (!mediaKey) {
            return
        }

        if (mediaKey === 'detailImageList') {
            const mappedDetailItem = toLegacyDetailImageListItem(imageItem)
            if (!mappedDetailItem) {
                return
            }

            normalizedImageMedia.detailImageList = mergeLegacyDetailImageIntoList(
                normalizedImageMedia.detailImageList,
                mappedDetailItem
            )
            return
        }

        if (hasMediaValue(normalizedImageMedia[mediaKey])) {
            return
        }

        normalizedImageMedia[mediaKey] = toLegacyLanguageMediaMap(imageItem)
    })

    return normalizedImageMedia
}

const normalizeCouponMedia = (imageMedia) => {
    const mergedMedia = _.cloneDeep(imageMedia || {})

    if (mergedMedia.detailPurchaseImage && !mergedMedia.cashCouponDetailPurchasedImage) {
        mergedMedia.cashCouponDetailPurchasedImage = _.cloneDeep(mergedMedia.detailPurchaseImage)
    }

    if (mergedMedia.leftImage && !mergedMedia.cashCouponLeftImage) {
        mergedMedia.cashCouponLeftImage = _.cloneDeep(mergedMedia.leftImage)
    }

    if (mergedMedia.cashCouponLeftImage && !mergedMedia.leftImage) {
        mergedMedia.leftImage = _.cloneDeep(mergedMedia.cashCouponLeftImage)
    }

    if (mergedMedia.detailImage && !Array.isArray(mergedMedia.detailImageList)) {
        const detailImageMap = normalizeMediaLanguageMap(mergedMedia.detailImage)
        mergedMedia.detailImageList = detailImageMap ? [detailImageMap] : []
    }

    delete mergedMedia.detailPurchaseImage;
    delete mergedMedia.detailImage;

    ['iconImage', 'cashCouponDetailImage', 'cashCouponDetailPurchasedImage', 'leftImage', 'cashCouponLeftImage'].forEach((mediaKey) => {
        mergedMedia[mediaKey] = normalizeMediaLanguageMap(mergedMedia[mediaKey])
    })

    mergedMedia.detailImageList = normalizeDetailImageList(mergedMedia.detailImageList)

    return mergedMedia
}

const normalizeProductMediaFields = (productDraft) => {
    if (!productDraft || typeof productDraft !== 'object') {
        return productDraft
    }

    const migratedLegacyImageMedia = convertLegacyImageListToImageMedia(
        productDraft.imageList,
        productDraft.imageMedia
    )

    const normalizedImageMedia = normalizeCouponMedia(
        migratedLegacyImageMedia
    )

    if (!_.isEmpty(normalizedImageMedia)) {
        productDraft.imageMedia = normalizedImageMedia
    } else if (!productDraft.imageMedia) {
        productDraft.imageMedia = {}
    }

    return productDraft
}

const syncActiveProductMediaToLists = (draft) => {
    if (!draft?.product?.productCode || !draft?.product?.imageMedia) {
        return draft
    }

    const productCode = draft.product.productCode
    const normalizedImageMedia = _.cloneDeep(draft.product.imageMedia)
    const normalizedImageList = _.cloneDeep(draft.category.imageList)

    if (Array.isArray(draft.productList)) {
        draft.productList = draft.productList.map((productItem) => {
            if (productItem?.productCode === productCode) {
                const normalizedProductItem = normalizeProductMediaFields({
                    ...productItem,
                    imageMedia: _.cloneDeep(normalizedImageMedia),
                    imageList: _.cloneDeep(normalizedImageList),
                })

                return normalizedProductItem
            }

            return normalizeProductMediaFields(productItem)
        })
    }

    if (Array.isArray(draft.subProductList)) {
        draft.subProductList = draft.subProductList.map((productItem) => {
            if (productItem?.productCode === productCode) {
                const normalizedProductItem = normalizeProductMediaFields({
                    ...productItem,
                    imageMedia: _.cloneDeep(normalizedImageMedia),
                })

                return normalizedProductItem
            }

            return normalizeProductMediaFields(productItem)
        })
    }

    return draft
}

const normalizeProductState = (product) => {

    const normalizedProduct = _.cloneDeep(product)

    if (!normalizedProduct?.draft) {
        return normalizedProduct
    }

    if (normalizedProduct.draft.product) {
        normalizedProduct.draft.product = normalizeProductMediaFields(normalizedProduct.draft.product)
    }

    if (Array.isArray(normalizedProduct.draft.productList)) {
        normalizedProduct.draft.productList = normalizedProduct.draft.productList.map((productItem) => normalizeProductMediaFields(productItem))
    }

    if (Array.isArray(normalizedProduct.draft.subProductList)) {
        normalizedProduct.draft.subProductList = normalizedProduct.draft.subProductList.map((productItem) => normalizeProductMediaFields(productItem))
    }

    normalizedProduct.draft = syncActiveProductMediaToLists(normalizedProduct.draft)

    return normalizedProduct
}

export const setProductDefault = () => {
    return {
        type: PRODUCT.SET_PRODUCT_DEFAULT,
    }
}

export const setProduct = (product) => {
    try {
        const newProduct = normalizeProductState(product)

        return {
            type: PRODUCT.SET_PRODUCT,
            product: newProduct,
        }
    } catch (e) {
        return {
            type: PRODUCT.SET_PRODUCT,
            product: product,
        }
    }
}

export const setProductStepError = (productStepError) => {
    return {
        type: PRODUCT.SET_PRODUCT_STEP_ERROR_VALUE,
        productStepError: productStepError,
    }
}

export const setProductValue = (draftType, originalProduct, key, value, index, type) => {
    let product = normalizeProductState(originalProduct)

    const setRedeemInfo = (existing = false) => {
        product.draft.redeemGroup = value.redeemGroup
        if (existing) {
            product.draft.existingRedeemGroupList = value.existingRedeemGroupList
            return
        }
        product.draft.redeemGroupList = value.redeemGroupList
    }

    const setProductInfo = () => {
        product.draft.product = value.product
        product.draft.productList = value.productList
        product.draft.subProductList = value.subProductList
        product.draft.productCode = ''
        product.draft.lotInventorySet = false
        product.draft.lotInventory = null
    }

    const setLotInventory = (isSub = false) => {
        if (isSub) {
            product.draft.subProductList = value.subProductList
        } else {
            product.draft.productList = value.productList
            product.draft.redeemCodeApiType = value.redeemCodeApiType
        }

        product.draft.productCode = value.productCode
        product.draft.productType = value.productType
        product.draft.lotInventory = value.lotInventory
        product.draft.lotInventorySet = value.lotInventorySet
    }

    const setLeftImage = () => {
        if (!product.draft.product) {
            return
        }
        if (!product.draft.product.imageMedia) {
            product.draft.product.imageMedia = {}
        }
        product.draft.product.imageMedia.cashCouponLeftImage = value
        product.draft = syncActiveProductMediaToLists(product.draft)
    }

    const draftTypeHandlers = {
        PRODUCT: () => {
            product = setProductProduct(product, key, value, index)
        },
        CAT: () => {
            product.draft.category = setProductCategory(product.draft.category, key, value)
        },
        REDEEM: () => {
            product = setProductRedeem(product, key, value)
        },
        REDEEM_INFO: () => {
            setRedeemInfo(false)
        },
        EXISTING_REDEEM_GROUP: () => {
            setRedeemInfo(true)
        },
        PRODUCT_INFO: () => {
            setProductInfo()
        },
        LOT_INVENTORY: () => {
            setLotInventory(false)
        },
        LOT_INVENTORY_SUB: () => {
            setLotInventory(true)
        },
        DRAFT: () => {
            product.draft[key] = value
        },
        LEFT_IMAGE: () => {
            setLeftImage()
        },
    }

    if (draftTypeHandlers[draftType]) {
        draftTypeHandlers[draftType]()
    } else {
        product.draft = value
    }

    if (type !== 'delete' && product.draft.product && product.draft.product.productCode) {
        product.draft = productSave(_.cloneDeep(product))
    }

    return {
        type: PRODUCT.SET_PRODUCT_VALUE,
        product: product,
    }
}

export const setProductCategory = (category, key, value) => {
    const guluLang = store.getState().lang.ui.lang
    const newCategory = _.cloneDeep(category)
    const isSelectedPreRegion = newCategory.regionCode === value?.regionCode

    switch (key) {
        case 'channelNameTranslate':
            newCategory.tcName = value.tcName
            newCategory.scName = value.scName
            break
        case 'descriptionTranslate':
            newCategory.tcDescription = value.tcDescription
            newCategory.scDescription = value.scDescription
            break
        case 'scNameDisable':
            newCategory[key] = value

            if (value) {
                newCategory.scName = newCategory.tcName
                    ? sify(newCategory.tcName)
                    : newCategory.tcName
            }

            break
        case 'scDescriptionDisable':
            newCategory[key] = value
            if (value) {
                newCategory.scDescription = newCategory.tcDescription
                    ? sify(newCategory.tcDescription)
                    : newCategory.tcDescription
            }
            break
        case 'channelSelect':
            newCategory.categoryCode = value.categoryCode
            newCategory.channelSave = value.channelSave
            break
        case 'channelCreate':
            newCategory.channel = value.channel
            newCategory.channelSave = value.channelSave
            break
        case 'refCategory':
            newCategory.refCategoryCode = value.refCategoryCode
            newCategory.refCategoryTcName = value.refCategoryTcName
            newCategory.refCategoryId = value.refCategoryId
            break
        case PRODUCT_CATEGORY_ACTIONS.SET_REGION:
            newCategory.regionCode = value?.regionCode || ''
            newCategory.regionName = getI18nObjValue(guluLang, value?.name) || ''
            newCategory.cityCode = isSelectedPreRegion ? newCategory?.cityCode : null
            newCategory.cityName = isSelectedPreRegion ? newCategory?.cityName : null
            break
        case PRODUCT_CATEGORY_ACTIONS.SET_CITY:
            newCategory.cityCode = value?.cityCode || ''
            newCategory.cityName = getI18nObjValue(guluLang, value?.name) || ''
            break
        default:
            newCategory[key] = value
    }

    return {
        type: PRODUCT.SET_PRODUCT_CATEGORY,
        category: newCategory,
    }
}

const setProductRedeem = (product, key, value) => {
    const originalProduct = _.cloneDeep(product)
    const redeem = _.cloneDeep(originalProduct.draft.redeemGroup)

    switch (key) {
        // case "changeRedeemType":
        //     redeem.type = value.type;
        //     redeem.startRedeemTimestamp = value.startRedeemTimestamp;
        //     redeem.endRedeemTimestamp = value.endRedeemTimestamp;
        //     redeem.startAfterDay = value.startAfterDay;
        //     redeem.endAfterDay = value.endAfterDay;
        //     break;
        case 'changeRedeemType':
            redeem.type = value.type
            redeem.startRedeemTimestamp = value.startRedeemTimestamp
            redeem.endRedeemTimestamp = value.endRedeemTimestamp
            redeem.startAfterDay = value.startAfterDay
            redeem.endAfterDay = value.endAfterDay
            redeem.selectLocation = value.selectLocation
            redeem.selectDate = value.selectDate
            redeem.selectTime = value.selectTime
            redeem.redeemLocationList = value.redeemLocationList
            break
        // case "changeSelectLocation":
        //     redeem.selectLocation = value.selectLocation;
        //     redeem.selectDate = value.selectDate;
        //     redeem.selectTime = value.selectTime;
        //     redeem.redeemLocationList = value.redeemLocationList;
        //     break;
        default:
            redeem[key] = value
    }
    originalProduct.draft.redeemGroup = redeem

    originalProduct.draft.redeemGroupList = originalProduct.draft.redeemGroupList.map(
        (tempRedeemGroup) => {
            if (tempRedeemGroup.redeemGroupCode === redeem.redeemGroupCode) {
                tempRedeemGroup = redeem
            }

            return tempRedeemGroup
        }
    )

    return originalProduct
}

const setProductProduct = (product, key, value, index) => {
    const originalProduct = _.cloneDeep(product)
    const tempProductDraft = normalizeProductMediaFields(_.cloneDeep(product.draft.product))
    const normalizedKey = key

    if (!tempProductDraft.imageMedia) {
        tempProductDraft.imageMedia = {}
    }

    switch (normalizedKey) {
        case 'brandCode':
            tempProductDraft.brandCode = value.brandCode
            tempProductDraft.brandTcName = value.brandTcName
            tempProductDraft.brandName = value.brandTcName
            break
        case 'systemGroupId':
            tempProductDraft.systemGroupId = value.systemGroupId
            tempProductDraft.systemGroupName = value.systemGroupName
            break
        case 'packageType':
            tempProductDraft.type = value.type
            tempProductDraft.subProductMapList = value.subProductMapList
            tempProductDraft.redeemGroupCode = value.redeemGroupCode
            tempProductDraft.searchable = value.searchable
            tempProductDraft.shareable = value.shareable
            tempProductDraft.productCode = value.productCode
            tempProductDraft.packageCashCoupon = value.packageCashCoupon
            break
        case 'productNameTranslate':
            tempProductDraft.tcName = value
            tempProductDraft.scName = sify(value)
            break
        case 'productDescriptionTranslate':
            tempProductDraft.tcDescription = value
            tempProductDraft.scDescription = sify(value)
            break
        case 'productTacTranslate':
            tempProductDraft.tcTac = value
            tempProductDraft.scTac = sify(value)
            break
        case 'productRedeemDescriptionTranslate':
            tempProductDraft.tcRedeemDescription = value
            tempProductDraft.scRedeemDescription = sify(value)
            break
        case 'productDiscountDescriptionTranslate':
            tempProductDraft.tcDiscountDescription = value
            tempProductDraft.scDiscountDescription = sify(value)
            break
        case 'scNameDisable':
            tempProductDraft[key] = value
            if (value) {
                tempProductDraft.scName = tempProductDraft.tcName
                    ? sify(tempProductDraft.tcName)
                    : tempProductDraft.tcName
            }
            break
        case 'scDescriptionDisable':
            tempProductDraft[key] = value
            if (value) {
                tempProductDraft.scDescription = tempProductDraft.tcDescription
                    ? sify(tempProductDraft.tcDescription)
                    : tempProductDraft.tcDescription
            }
            break
        case 'scTacDisable':
            tempProductDraft[key] = value
            if (value) {
                tempProductDraft.scTac = tempProductDraft.tcTac
                    ? sify(tempProductDraft.tcTac)
                    : tempProductDraft.tcTac
            }
            break
        case 'scRedeemDescriptionDisable':
            tempProductDraft[key] = value
            if (value) {
                tempProductDraft.scRedeemDescription = tempProductDraft.tcRedeemDescription
                    ? sify(tempProductDraft.tcRedeemDescription)
                    : tempProductDraft.tcRedeemDescription
            }
            break
        case 'scDiscountDescriptionDisable':
            tempProductDraft[key] = value
            if (value) {
                tempProductDraft.scDiscountDescription = tempProductDraft.tcDiscountDescription
                    ? sify(tempProductDraft.tcDiscountDescription)
                    : tempProductDraft.tcDiscountDescription
            }
            break
        default:
            if (normalizedKey === 'imageMedia') {
                tempProductDraft.imageMedia = normalizeCouponMedia(value)
            } else if (normalizedKey.includes('.')) {
                _.set(tempProductDraft, normalizedKey, value)
            } else {
                tempProductDraft[normalizedKey] = value
            }
    }

    normalizeProductMediaFields(tempProductDraft)

    originalProduct.draft.product = tempProductDraft

    originalProduct.draft.productList = originalProduct.draft.productList.map((tempProduct) => {
        if (tempProduct.productCode === tempProductDraft.productCode) {
            tempProduct = tempProductDraft
        }

        return tempProduct
    })

    originalProduct.draft.subProductList = originalProduct.draft.subProductList.map(
        (tempSubProduct) => {
            if (tempSubProduct.productCode === tempProductDraft.productCode) {
                tempSubProduct = tempProductDraft
            }

            return tempSubProduct
        }
    )

    return originalProduct
}
